<template>
  <div class="monthly">
    <header class="monthly__hero">
      <div>
        <h1 class="al-display">Monthly Summary</h1>
        <p class="al-subtle" style="margin-top: 0.5rem">
          {{ summary?.label || 'Loading…' }}
        </p>
      </div>
      <n-date-picker
        v-model:value="selectedMonth"
        type="month"
        :on-update:value="onMonthChange"
      />
    </header>

    <div v-if="loading" class="monthly__loading">
      <n-spin size="large" />
    </div>

    <template v-else-if="summary">
      <!-- KPI row -->
      <section class="monthly__kpis">
        <article class="al-kpi">
          <span class="material-symbols-outlined al-kpi__watermark al-kpi__watermark--primary">
            payments
          </span>
          <h3 class="al-kpi__label">Income</h3>
          <div class="al-kpi__value al-kpi__value--positive">
            {{ formatCurrency(summary.income) }}
          </div>
          <div class="al-kpi__delta" :class="deltaClass(summary.delta_income_pct, true)">
            <span class="material-symbols-outlined" style="font-size: 16px">
              {{ trendIcon(summary.delta_income_pct, true) }}
            </span>
            {{ deltaLabel(summary.delta_income_pct) }} vs last month
          </div>
        </article>

        <article class="al-kpi">
          <span class="material-symbols-outlined al-kpi__watermark al-kpi__watermark--tertiary">
            credit_card
          </span>
          <h3 class="al-kpi__label">Total Expenses</h3>
          <div class="al-kpi__value">{{ formatCurrency(summary.expenses_total) }}</div>
          <div class="al-kpi__delta" :class="deltaClass(summary.delta_expenses_pct, false)">
            <span class="material-symbols-outlined" style="font-size: 16px">
              {{ trendIcon(summary.delta_expenses_pct, false) }}
            </span>
            {{ deltaLabel(summary.delta_expenses_pct) }} vs last month
          </div>
        </article>

        <article class="al-kpi">
          <span
            class="material-symbols-outlined al-kpi__watermark"
            :class="summary.net >= 0 ? 'al-kpi__watermark--primary' : 'al-kpi__watermark--tertiary'"
          >
            account_balance
          </span>
          <h3 class="al-kpi__label">Net Balance</h3>
          <div
            class="al-kpi__value"
            :class="summary.net >= 0 ? 'al-kpi__value--positive' : 'monthly__value--negative'"
          >
            {{ formatCurrency(summary.net) }}
          </div>
          <div class="al-kpi__delta" :class="deltaClass(summary.delta_net_pct, true)">
            <span class="material-symbols-outlined" style="font-size: 16px">
              {{ trendIcon(summary.delta_net_pct, true) }}
            </span>
            {{ deltaLabel(summary.delta_net_pct) }} vs last month
          </div>
        </article>
      </section>

      <!-- Detail grid -->
      <section class="monthly__grid">
        <!-- Expenses split -->
        <div class="al-card monthly__card">
          <h2 class="al-title" style="margin: 0 0 1.25rem">Expenses Breakdown</h2>
          <div class="monthly__split-row">
            <div class="monthly__split-item">
              <span class="al-subtle">Individual</span>
              <span class="al-tabular">{{ formatCurrency(summary.expenses_individual) }}</span>
            </div>
            <div class="monthly__split-divider" />
            <div class="monthly__split-item">
              <span class="al-subtle">Shared</span>
              <span class="al-tabular">{{ formatCurrency(summary.expenses_shared) }}</span>
            </div>
          </div>
        </div>

        <!-- Categories -->
        <div class="al-card monthly__card">
          <h2 class="al-title" style="margin: 0 0 1.25rem">By Category</h2>
          <p v-if="sortedCategories.length === 0" class="al-subtle">No expenses this month.</p>
          <div v-else class="monthly__cats">
            <div
              v-for="[cat, amount] in sortedCategories"
              :key="cat"
              class="monthly__cat-row"
            >
              <n-tag size="small" :color="getCategoryColor(cat)" style="min-width: 96px; justify-content: center">
                {{ cat }}
              </n-tag>
              <div class="monthly__bar-wrap">
                <div class="monthly__bar" :style="{ width: barPct(amount) + '%' }" />
              </div>
              <span class="al-tabular monthly__cat-amount">{{ formatCurrency(amount) }}</span>
            </div>
          </div>
        </div>

        <!-- Previous month -->
        <div class="al-card monthly__card" v-if="summary.prev_month">
          <h2 class="al-title" style="margin: 0 0 1.25rem">Last Month</h2>
          <div class="monthly__split-row monthly__split-row--three">
            <div class="monthly__split-item">
              <span class="al-subtle">Income</span>
              <span class="al-tabular">{{ formatCurrency(summary.prev_month.income) }}</span>
            </div>
            <div class="monthly__split-item">
              <span class="al-subtle">Expenses</span>
              <span class="al-tabular">{{ formatCurrency(summary.prev_month.expenses) }}</span>
            </div>
            <div class="monthly__split-item">
              <span class="al-subtle">Net</span>
              <span
                class="al-tabular"
                :class="summary.prev_month.net >= 0 ? 'monthly__text--positive' : 'monthly__text--negative'"
              >
                {{ formatCurrency(summary.prev_month.net) }}
              </span>
            </div>
          </div>
        </div>
      </section>
    </template>

    <div v-else-if="error" class="al-subtle" style="text-align: center; padding: 3rem">
      {{ error }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { NSpin, NDatePicker, NTag } from 'naive-ui';
import { useMonthlyStore } from '../stores/monthlyStore';
import { formatCurrency, getCategoryColor } from '../utils/format';

const monthlyStore = useMonthlyStore();
const summary = computed(() => monthlyStore.summary);
const loading = computed(() => monthlyStore.loading);
const error = computed(() => monthlyStore.error);

const now = new Date();
const selectedMonth = ref(now.getTime());

onMounted(() => {
  monthlyStore.fetchMonthlySummary(now.getFullYear(), now.getMonth() + 1);
});

const onMonthChange = (value) => {
  if (!value) return;
  const d = new Date(value);
  monthlyStore.fetchMonthlySummary(d.getFullYear(), d.getMonth() + 1);
};

const sortedCategories = computed(() => {
  if (!summary.value) return [];
  return Object.entries(summary.value.by_category).sort((a, b) => b[1] - a[1]);
});

const maxCategoryAmount = computed(() =>
  sortedCategories.value.length ? sortedCategories.value[0][1] : 1
);

const barPct = (amount) =>
  Math.round((amount / maxCategoryAmount.value) * 100);

const deltaLabel = (pct) => {
  if (pct === null || pct === undefined) return '—';
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct}%`;
};

const deltaClass = (pct, higherIsBetter) => {
  if (pct === null || pct === undefined) return '';
  const positive = higherIsBetter ? pct > 0 : pct < 0;
  return positive ? 'al-kpi__delta--positive' : 'al-kpi__delta--negative';
};

const trendIcon = (pct, higherIsBetter) => {
  if (pct === null || pct === undefined) return 'trending_flat';
  if (pct === 0) return 'trending_flat';
  const up = pct > 0;
  if (higherIsBetter) return up ? 'trending_up' : 'trending_down';
  return up ? 'trending_up' : 'trending_down';
};
</script>

<style scoped>
.monthly {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.monthly__hero {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.5rem;
}

.monthly__loading {
  display: flex;
  justify-content: center;
  padding: 4rem;
}

.monthly__kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
}

.monthly__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  align-items: start;
}

.monthly__card {
  padding: 1.5rem;
}

.monthly__value--negative {
  color: var(--al-error, #dc2626);
}

/* Split rows */
.monthly__split-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
}
.monthly__split-row--three {
  grid-template-columns: repeat(3, 1fr);
}
.monthly__split-item {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.monthly__split-divider {
  width: 1px;
  height: 2.5rem;
  background: rgba(228, 225, 231, 0.15);
}

/* Category bars */
.monthly__cats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.monthly__cat-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.monthly__bar-wrap {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: rgba(228, 225, 231, 0.1);
  overflow: hidden;
}
.monthly__bar {
  height: 100%;
  border-radius: 3px;
  background: var(--al-primary, #a78bfa);
  transition: width 0.4s ease;
}
.monthly__cat-amount {
  min-width: 80px;
  text-align: right;
  font-size: 0.875rem;
}

/* Net text colors */
.monthly__text--positive { color: var(--al-primary, #a78bfa); }
.monthly__text--negative { color: var(--al-error, #dc2626); }

@media (max-width: 1100px) {
  .monthly__kpis,
  .monthly__grid {
    grid-template-columns: 1fr;
  }
  .monthly__split-row {
    grid-template-columns: 1fr;
  }
  .monthly__split-divider { display: none; }
}
</style>
